'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import api from '@/lib/api';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';

// Define the user type based on your LoginResponse
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  instituteId?: string;
}

// Routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password'];

const PARENT_AUTH_TOKEN_KEY = 'parent_accessToken';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (userData: User, authToken: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  // Check if current route is public
  const isPublicRoute = PUBLIC_ROUTES.includes(pathname);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      // Check localStorage first, then cookies as fallback
      let storedToken = typeof window !== 'undefined' ? localStorage.getItem(PARENT_AUTH_TOKEN_KEY) : null;

      // If no token in localStorage, check cookies
      if (!storedToken && typeof document !== 'undefined') {
        const tokenCookie = document.cookie
          .split('; ')
          .find((row) => row.startsWith(`${PARENT_AUTH_TOKEN_KEY}=`));
        storedToken = tokenCookie ? tokenCookie.split('=')[1] : null;
      }

      if (storedToken) {
        try {
          const storedUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            // Validate user data structure
            if (userData && userData.id && userData.email && userData.role) {
              setUser(userData);
              setToken(storedToken);
              api.defaults.headers.common['Authorization'] =
                `Bearer ${storedToken}`;
              console.log(
                'Auth initialized successfully with role:',
                userData.role,
              );
            } else {
              throw new Error('Invalid user data structure');
            }
          } else {
            throw new Error('No user data found');
          }
        } catch (error) {
          // console.error('Failed to initialize auth from stored token', error);
          if (typeof window !== 'undefined') {
            localStorage.removeItem(PARENT_AUTH_TOKEN_KEY);
            localStorage.removeItem('user');
            // Clear any persisted student selection when auth init fails
            localStorage.removeItem('selectedStudentId');
          }
          if (typeof document !== 'undefined') {
            document.cookie =
              `${PARENT_AUTH_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
          }
          delete api.defaults.headers.common['Authorization'];
          if (!isPublicRoute) {
            router.push('/login');
          }
        }
      } else {
        // No token found, ensure user is logged out and redirect to login
        if (typeof window !== 'undefined') {
          localStorage.removeItem('user');
          // Also clear any persisted student selection when not authenticated
          localStorage.removeItem('selectedStudentId');
        }
        if (typeof document !== 'undefined') {
          document.cookie =
            `${PARENT_AUTH_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        }
        delete api.defaults.headers.common['Authorization'];
        if (!isPublicRoute) {
          router.push('/login');
        }
      }
      setIsLoading(false);
    };
    initializeAuth();
  }, [router, isPublicRoute]);

  const login = (userData: User, authToken: string) => {
    // Clear any previous session's selected student
    if (typeof window !== 'undefined') {
      localStorage.removeItem('selectedStudentId');

      localStorage.setItem(PARENT_AUTH_TOKEN_KEY, authToken);
      localStorage.setItem('user', JSON.stringify(userData)); // Store user data
    }

    // Also set cookie for server-side middleware
    if (typeof document !== 'undefined') {
      document.cookie = `${PARENT_AUTH_TOKEN_KEY}=${authToken}; path=/parent; max-age=${process.env.NEXT_PUBLIC_AUTH_COOKIE_MAX_AGE_SECONDS || 7 * 24 * 60 * 60}; samesite=strict`;
    }

    setToken(authToken);
    setUser(userData);
    api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const logout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(PARENT_AUTH_TOKEN_KEY);
      localStorage.removeItem('user');
      // Clear any persisted student selection on logout
      localStorage.removeItem('selectedStudentId');
    }

    // Clear cookie as well
    if (typeof document !== 'undefined') {
      document.cookie =
        `${PARENT_AUTH_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
      // Also clear the cookie at the path it was set
      document.cookie =
        `${PARENT_AUTH_TOKEN_KEY}=; path=/parent; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    }

    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
    router.push('/login');
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await api.post('/auth/reset-password', { token, newPassword });
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        logout,
        isAuthenticated: !!token && !!user && user.role === 'PARENT',
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
